import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Zap, TrendingUp, ChevronDown, MessageCircle } from "lucide-react";

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "100% DSGVO-Konform" },
  { icon: Zap, label: "Blitzschnelle Ladezeiten" },
  { icon: TrendingUp, label: "Messbares Wachstum" },
];

export const Hero = () => {
  return (
    <>
      <section
        id="top"
        data-testid="hero-section"
        className="relative flex min-h-screen items-center overflow-hidden bg-[#0A0A0A]"
      >
        {/* High-Performance Pure CSS Cinematic Dark Ambient Glow (0ms GPU lag, native gradient) */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          {/* Subtle gold radial glow top right */}
          <div
            className="absolute -top-40 right-0 h-[600px] w-[600px] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(197, 160, 89, 0.08) 0%, transparent 70%)" }}
          />
          {/* Deep dark glow bottom left */}
          <div
            className="absolute -bottom-40 -left-20 h-[500px] w-[500px] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(255, 255, 255, 0.03) 0%, transparent 70%)" }}
          />
          {/* Ultra-subtle grid lines */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)`,
              backgroundSize: "64px 64px",
            }}
          />
        </div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-36 sm:px-8 lg:pb-32 lg:pt-44">
          <div className="max-w-3xl">
            {/* Top pill badge */}
            <div className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-[#141418] px-4 py-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#C5A059] animate-pulse" />
              <span className="uppercase tracking-wider text-xs font-semibold text-[#C5A059]">
                DIGITALAGENTUR LÜDENSCHEID
              </span>
            </div>

            {/* Main H1 - immediate render for instant LCP / FCP */}
            <h1 className="font-heading text-4xl font-semibold leading-[1.15] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Starke digitale Präsenz.{" "}
              <span className="text-[#C5A059]">Messbarer Erfolg.</span>
              <span className="mt-4 block text-xl font-normal text-muted-foreground sm:text-2xl lg:text-3xl">
                Webdesign, Branding & Marketing aus Lüdenscheid.
              </span>
            </h1>

            {/* Paragraph */}
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Wir verhelfen regionalen Unternehmen im Märkischen Kreis & Sauerland zu einer
              einprägsamen Marke und hochkonvertierenden Webseiten. Höchste Qualität,
              persönlicher Ansprechpartner und ohne unnötige Agentur-Umlenkungen.
            </p>

            {/* CTA Buttons */}
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Button
                asChild
                size="lg"
                data-testid="hero-primary-cta"
                className="group h-14 rounded-full bg-[#C5A059] px-8 py-6 text-base font-semibold text-[#0A0A0A] hover:bg-[#b08e4c] transition-all duration-200 shadow-lg shadow-[#C5A059]/20 hover:shadow-[#C5A059]/30"
              >
                <a href="/#kontakt">
                  Projekt unverbindlich anfragen
                  <ArrowRight className="ml-1.5 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                data-testid="hero-whatsapp-cta"
                className="h-14 rounded-full border border-white/20 bg-white/5 px-8 py-6 text-base font-semibold text-white transition-all duration-200 hover:border-emerald-500/50 hover:bg-emerald-500/5 hover:text-emerald-400"
              >
                <a
                  href="https://wa.me/491748367114"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Direkt per WhatsApp anfragen"
                >
                  <MessageCircle className="mr-1.5 h-4 w-4" />
                  Direkt per WhatsApp
                </a>
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="mt-14 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
              {TRUST_BADGES.map((badge) => (
                <div key={badge.label} className="flex items-center gap-2.5" data-testid="trust-badge">
                  <badge.icon className="h-4 w-4 text-[#C5A059]" />
                  <span className="text-sm font-medium text-foreground/80">{badge.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        <a
          href="/#leistungen"
          aria-label="Nach unten scrollen"
          className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 text-muted-foreground transition-colors duration-300 hover:text-[#C5A059] md:block"
        >
          <ChevronDown className="h-6 w-6 animate-bounce" />
        </a>
      </section>

      {/* Sticky Mobile CTA Bar */}
      <div
        data-testid="sticky-mobile-cta"
        className="fixed bottom-0 inset-x-0 z-40 lg:hidden border-t border-white/10 bg-[#0A0A0A] px-4 py-3 shadow-2xl"
      >
        <div className="flex gap-3">
          <a
            href="/#kontakt"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#C5A059] py-3 text-sm font-semibold text-[#0A0A0A] shadow-lg shadow-[#C5A059]/20 transition-all active:scale-95"
          >
            <ArrowRight className="h-4 w-4" />
            Projekt anfragen
          </a>
          <a
            href="https://wa.me/491748367114"
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#25D366] shadow-lg shadow-[#25D366]/20 transition-all active:scale-95"
            aria-label="Per WhatsApp kontaktieren"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </a>
        </div>
      </div>
    </>
  );
};
